<template>
  <div class="page-documentation">
    <landing-header />

    <main>
      <section class="hero-tech">
        <div class="max-w-7xl mx-auto px-4 flex flex-col lg:flex-row items-center gap-16">
          <div class="lg:w-1/2">
            <span class="badge-tech">Base de données technique</span>
            <h1 class="text-4xl md:text-6xl font-black text-gray-900 mb-6 leading-tight">
              Toute votre expertise <br />
              <span class="gradient-text">à portée de main</span>
            </h1>
            <p class="text-xl text-gray-600 mb-8 leading-relaxed">
              Ne perdez plus de temps à chercher une notice ou un schéma. Termoflow centralise les documents techniques pour sécuriser vos interventions et professionnaliser vos équipes.
            </p>
            <div class="flex flex-wrap gap-4">
              <a href="/contact"><button class="btn-primary">Demander une démonstration</button></a>
              <div class="stats-mini">
                <span class="font-bold text-orange-600">+72k</span>
                <span class="text-xs text-gray-500 ml-1 uppercase font-bold tracking-tighter">Modèles d'appareils</span>
              </div>
            </div>
          </div>
          
          <div class="lg:w-1/2 w-full">
            <div class="mobile-mockup-container">
              <div class="mobile-frame shadow-2xl">
                <div class="mobile-screen">
                  <div class="screen-header">Notice Technique</div>
                  <div class="doc-preview">
                    <div class="doc-line title"></div>
                    <div class="doc-line md"></div>
                    <div class="doc-circle"></div>
                    <div class="doc-line lg"></div>
                  </div>
                </div>
              </div>
              <div class="floating-card">
                <i class="fa-solid fa-file-pdf text-red-500 mb-2 text-xl"></i>
                <p class="text-[10px] font-bold">VUE_ECLATEE.PDF</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="py-16 bg-gray-900 text-white overflow-hidden">
        <div class="max-w-7xl mx-auto px-4">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-12 items-center">
            <div class="text-center md:text-left">
              <h2 class="text-3xl font-bold mb-4">Une base exhaustive</h2>
              <p class="text-gray-400">Couvrant toutes les grandes marques du marché : Chaudières, PAC, Clim, Froid.</p>
            </div>
            <div class="stat-item">
              <div class="stat-number">72 000</div>
              <div class="stat-label">Modèles d'appareils</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">194 000</div>
              <div class="stat-label">Documents techniques</div>
            </div>
          </div>
        </div>
      </section>

      <section class="py-24 bg-white">
        <div class="max-w-7xl mx-auto px-4 space-y-32">
          
          <div class="flex flex-col lg:flex-row items-center gap-16">
                <div class="lg:w-1/2">
                    <div class="feature-tag">Mobilité & Support</div>
                    <h2 class="text-3xl font-bold text-gray-900 mb-6">Documentation directement liée aux interventions</h2>
                    <p class="text-gray-600 mb-6">Le technicien accède immédiatement aux notices adaptées au modèle exact de l'appareil qu'il a devant lui, même sans connexion réseau.</p>
                    
                    <ul class="benefit-check-list">
                    <li><i class="fa-solid fa-circle-check"></i> Accès par simple scan ou recherche</li>
                    <li><i class="fa-solid fa-circle-check"></i> Notices d'installation, d'utilisation et schémas</li>
                    
                    <li class="highlight-benefit">
                        <i class="fa-solid fa-phone-volume text-orange-600"></i> 
                        <strong>Contact fournisseur direct :</strong> Accès au numéro du support technique selon la marque de l'appareil.
                    </li>
                    </ul>
                </div>
                
                <div class="lg:w-1/2 grid grid-cols-2 gap-4">
                    <div class="doc-card">Notices Installation</div>
                    <div class="doc-card">Schémas Tech</div>
                    <div class="doc-card">Documents SAV</div>
                    <div class="doc-card support-highlight">
                    <i class="fa-solid fa-headset mr-2"></i> Support Fabricant
                    </div>
                </div>
            </div>

          <div class="flex flex-col lg:flex-row-reverse items-center gap-16">
            <div class="lg:w-1/2">
              <div class="feature-tag">Traçabilité</div>
              <h2 class="text-3xl font-bold text-gray-900 mb-6">Historique technique par appareil</h2>
              <p class="text-gray-600 mb-6">Chaque appareil dispose de son propre "carnet de santé". Une vision historique essentielle pour la continuité de service et le SAV.</p>
              <div class="timeline-simple">
                <div class="timeline-point">Interventions réalisées</div>
                <div class="timeline-point">Opérations de maintenance</div>
                <div class="timeline-point">CERFA associés</div>
              </div>
            </div>
            <div class="lg:w-1/2 bg-gray-50 p-10 rounded-[2rem] border border-gray-100">
              <i class="fa-solid fa-microchip text-7xl text-gray-200"></i>
              <p class="mt-6 text-sm text-gray-400 font-bold uppercase tracking-widest">Fiche Identité Appareil</p>
            </div>
          </div>
        </div>
      </section>

      <section class="py-24 bg-gray-50">
        <div class="max-w-7xl mx-auto px-4">
          <div class="bg-white rounded-[3rem] p-12 shadow-sm border border-gray-100">
            <div class="text-center max-w-3xl mx-auto mb-16">
              <h2 class="text-3xl font-bold text-gray-900 mb-4">Un outil au service de la performance terrain</h2>
              <p class="text-gray-600">Réduisez les erreurs et les interventions non conformes en donnant l'information juste au bon moment.</p>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-12">
              <div class="perf-item text-center">
                <div class="perf-icon"><i class="fa-solid fa-phone-slash"></i></div>
                <h4 class="font-bold mb-2">Moins d'appels au bureau</h4>
                <p class="text-sm text-gray-500">Le technicien est autonome avec toute la data constructeur en poche.</p>
              </div>
              <div class="perf-item text-center">
                <div class="perf-icon"><i class="fa-solid fa-clock-rotate-left"></i></div>
                <h4 class="font-bold mb-2">Interventions plus rapides</h4>
                <p class="text-sm text-gray-500">Moins de recherches manuelles, plus de temps de dépannage réel.</p>
              </div>
              <div class="perf-item text-center">
                <div class="perf-icon"><i class="fa-solid fa-award"></i></div>
                <h4 class="font-bold mb-2">Expertise valorisée</h4>
                <p class="text-sm text-gray-500">Une qualité d'intervention homogène et conforme aux préconisations fabricants.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="py-24">
        <div class="max-w-7xl mx-auto px-4">
          <h2 class="text-center text-3xl font-bold mb-16">Les bénéfices concrets</h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div class="benefit-card">
              <i class="fa-solid fa-bolt"></i>
              <span>Accès immédiat à l'info</span>
            </div>
            <div class="benefit-card">
              <i class="fa-solid fa-shield-halved"></i>
              <span>Réduction des erreurs</span>
            </div>
            <div class="benefit-card">
              <i class="fa-solid fa-star"></i>
              <span>Meilleure satisfaction client</span>
            </div>
          </div>
        </div>
      </section>

      <section class="py-20 text-center">
        <div class="max-w-2xl mx-auto px-4">
          <h2 class="text-3xl font-bold text-gray-900 mb-8">Professionnalisez vos interventions dès aujourd'hui</h2>
          <div class="flex flex-col sm:flex-row justify-center gap-4">
             <a href="/contact"><button class="btn-primary-large">Demander une démonstration</button></a>
             <a href="/fonctionnalites" class="btn-outline-large">Voir les autres fonctions</a>
          </div>
        </div>
      </section>

      <landing-roi />
      <landing-avis />
    </main>

    <landing-footer />
  </div>
</template>

<script>
import LandingHeader from './LandingHeader.vue'
import LandingFooter from './LandingFooter.vue'
import LandingAvis from './LandingAvis.vue'
import LandingROI from './LandingROI.vue'

export default {
  name: 'DocTechniquePage',
  components: {
    LandingHeader,
    LandingFooter,
    LandingAvis,
    'landing-roi': LandingROI
  }
}
</script>

<style scoped>
/* --- TEXTE & DÉGRADÉS --- */
.gradient-text {
  background: linear-gradient(to right, #f97316, #ef4444);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* --- HERO --- */
.hero-tech {
  padding: 10rem 0 6rem;
  background: radial-gradient(circle at top right, #fff7ed, transparent 30%);
}

.badge-tech {
  display: inline-block;
  padding: 0.5rem 1rem;
  background-color: #fef2f2;
  color: #dc2626;
  font-weight: 800;
  font-size: 0.75rem;
  border-radius: 9999px;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
}

.stats-mini {
  display: flex;
  align-items: center;
  background: white;
  padding: 0.5rem 1.2rem;
  border-radius: 9999px;
  border: 1px solid #e5e7eb;
}

/* --- MOCKUP MOBILE --- */
.mobile-mockup-container {
  position: relative;
  display: flex;
  justify-content: center;
}

.mobile-frame {
  width: 280px;
  height: 500px;
  background: #1f2937;
  border-radius: 2.5rem;
  padding: 0.6rem;
}

.mobile-screen {
  background: white;
  width: 100%;
  height: 100%;
  border-radius: 2rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.screen-header {
  background: #f97316;
  color: white;
  padding: 1rem;
  font-size: 0.8rem;
  font-weight: 800;
  text-align: center;
}

.doc-preview { padding: 1.5rem; }
.doc-line { background: #f3f4f6; height: 0.5rem; border-radius: 1rem; margin-bottom: 0.75rem; }
.doc-line.title { width: 60%; background: #e5e7eb; height: 0.8rem; }
.doc-circle { width: 100%; aspect-ratio: 16/9; background: #f9fafb; border: 1px dashed #d1d5db; margin: 1.5rem 0; border-radius: 0.5rem; }

.floating-card {
  position: absolute;
  bottom: 2rem;
  right: -1rem;
  background: white;
  padding: 1rem;
  border-radius: 1rem;
  box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
  text-align: center;
  border: 1px solid #f3f4f6;
}

/* --- STATS SECTION --- */
.stat-item { text-align: center; }
.stat-number { font-size: 3rem; font-weight: 900; color: #f97316; line-height: 1; }
.stat-label { font-size: 0.8rem; text-transform: uppercase; font-weight: 700; color: #9ca3af; margin-top: 0.5rem; }

/* --- FEATURE ITEMS --- */
.feature-tag { color: #f97316; font-weight: 800; text-transform: uppercase; font-size: 0.75rem; margin-bottom: 0.75rem; }
.benefit-check-list { list-style: none; padding: 0; }
.benefit-check-list li { display: flex; align-items: center; gap: 0.75rem; font-weight: 700; color: #4b5563; margin-bottom: 0.75rem; }
.benefit-check-list li i { color: #f97316; font-size: 1.1rem; }

.doc-card {
  background: white;
  padding: 2rem;
  border-radius: 1.5rem;
  border: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-weight: 800;
  font-size: 0.9rem;
  color: #1f2937;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
}

.timeline-point {
  position: relative;
  padding-left: 2rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
}

.timeline-point::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 0.75rem;
  height: 0.75rem;
  background: #f97316;
  border-radius: 50%;
}

/* --- PERFORMANCE ITEMS --- */
.perf-icon {
  width: 4rem;
  height: 4rem;
  background: #fff7ed;
  color: #f97316;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin: 0 auto 1.5rem;
}

/* --- BENEFIT CARDS --- */
.benefit-card {
  background: white;
  padding: 1.5rem;
  border-radius: 1rem;
  border: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  gap: 1rem;
  font-weight: 800;
  transition: all 0.3s ease;
}

.benefit-card:hover { border-color: #f97316; transform: translateY(-3px); }
.benefit-card i { color: #f97316; }

/* --- BUTTONS --- */
.btn-primary {
  background: linear-gradient(to right, #f97316, #ef4444);
  color: white;
  padding: 1rem 2rem;
  border-radius: 9999px;
  font-weight: 700;
  border: none;
}

.btn-primary-large {
  background: #f97316;
  color: white;
  padding: 1.2rem 2.5rem;
  border-radius: 9999px;
  font-weight: 800;
  border: none;
}

.btn-outline-large {
  border: 2px solid #e5e7eb;
  padding: 1.2rem 2.5rem;
  border-radius: 9999px;
  font-weight: 800;
  color: #1f2937;
}

@media (max-width: 1024px) {
  .hero-tech { text-align: center; padding-top: 8rem; }
  .hero-tech .flex { flex-direction: column; }
  .mobile-mockup-container { margin-top: 4rem; }
}

.highlight-benefit {
  background: #fff7ed;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  border-left: 4px solid #f97316;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;
  color: #1f2937;
  margin-top: 1rem;
}

.support-highlight {
  background: #f97316 !important;
  color: white !important;
  border: none !important;
  box-shadow: 0 10px 15px -3px rgba(249, 115, 22, 0.3) !important;
}

.benefit-check-list li i {
  color: #f97316;
  font-size: 1.1rem;
}
</style>